import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { cardHref } from '@/lists/cellValue'
import { BonusDialog, KpiLines } from './EmployeeRecordView'
import { HistoryPanel } from './HistoryPanel'
import {
  fetchDashboard,
  fetchDefinitions,
  fetchDepartmentDashboard,
  fetchTargets,
  recomputeEmployee,
  setActual,
  type DashboardView,
  type TargetView,
} from './hrApi'
import {
  Field,
  FormModal,
  OutcomeAlerts,
  RefLink,
  Section,
  day,
  failureText,
  kpiValue,
  money,
  num,
  number,
  percent,
  text,
  today,
  useOutcome,
} from './hrShared'
import { Facts, Panel, RecordHeader, RecordTabs, StageBar, StateView, type Fact, type HeaderAction, type MenuAction } from './record'
import { labelOf, listPath } from './recordHelpers'
import type { RecordViewProps } from './types'
import '@/styles/record.css'

/**
 * Карточка цели KPI на дизайн-системе (EmployeeKpi.dc.html): показатели с весами,
 * план/факт/выполнение и премия.
 *
 * Запись — одна цель (показатель × период × сотрудник). Экран показывает её в контексте:
 * все показатели того же человека за тот же период дашбордом сервера, итоговое выполнение и
 * премию. Ни процент, ни вклад показателя, ни коэффициент здесь не считаются: всё приходит
 * с сервера — формула, повторённая для показа, разошлась бы с выплатой молча.
 *
 * Действия: «Начислить премию…» (`hr.kpi:approve`, фонд обязателен — его никто не
 * подставляет), «Пересчитать факт» (`update`, факт читается из источника заново — повтор
 * безопасен), «Задать факт вручную…» — только у показателя без источника: у выводимого
 * правка дожила бы до первого пересчёта, и сервер её отвергает.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - Статуса «утверждено» и его отмены, журнала утверждений, комментариев руководителя: в
 *   API нет ни утверждения KPI, ни его истории — только начисление премии.
 * - Вклада показателя в итог, порога и потолка схемы: сервер отдаёт выполнение строк и итог,
 *   но не вклад и не правила схемы; посчитать их здесь значило бы завести вторую формулу.
 * - Корректировки факта автоматического показателя: сервер принимает ручной факт только у
 *   показателя без источника.
 */
export function KpiRecordView({ recordId, record, context, form }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const values = record.values
  const [tab, setTab] = useState('lines')
  const [dialog, setDialog] = useState<'bonus' | 'actual' | null>(null)
  const employeeId = text(values, 'employee_id')
  const departmentId = text(values, 'department_id')
  const from = text(values, 'period_start')
  const to = text(values, 'period_end')
  const kpiId = text(values, 'kpi_id')
  const mayUpdate = can(user, 'hr.kpi', 'update')
  const mayApprove = can(user, 'hr.kpi', 'approve')

  const dashboard = useQuery({
    queryKey: ['hr-kpi-dashboard', employeeId ?? departmentId, from, to],
    enabled: from != null && to != null && (employeeId != null || departmentId != null),
    retry: false,
    queryFn: ({ signal }) =>
      employeeId
        ? fetchDashboard(employeeId, from as string, to as string, signal)
        : fetchDepartmentDashboard(departmentId as string, from as string, to as string, signal),
  })
  const targets = useQuery({
    queryKey: ['hr-targets', employeeId],
    queryFn: ({ signal }) => fetchTargets(employeeId as string, signal),
    enabled: employeeId != null,
    retry: false,
  })
  const definitions = useQuery({
    queryKey: ['hr-kpi-definitions'],
    queryFn: ({ signal }) => fetchDefinitions(signal),
    // Читается всем, кто видит карточку (сервер требует только read): из определения
    // берётся единица показателя, без которой план и факт — голые числа.
    retry: false,
  })

  const outcome = useOutcome([
    ['hr-kpi-dashboard', employeeId ?? departmentId, from, to],
    ['hr-targets', employeeId],
    ['record', 'hr.kpi', recordId],
    ['list', 'hr.kpi'],
  ])
  // Recompute "on" a day inside the period: the server recomputes the targets covering it,
  // and a future date would recompute nothing yet.
  const recomputeOn = to != null && to < today() ? to : today()
  const recompute = useMutation({
    mutationFn: () => recomputeEmployee(employeeId as string, recomputeOn),
    onSuccess: (result) => outcome.succeed(t('record.hr.kpi.recomputed', { count: result.changed })),
    onError: outcome.fail,
  })

  const target = (targets.data ?? []).find((row) => row.id === recordId) ?? null
  const definition = (definitions.data ?? []).find((row) => row.id === kpiId) ?? null
  const manual = definition != null && !definition.derivable
  const hasBonusColumn = context.columns.some((column) => column.name === 'bonus_amount')
  const bonusAmount = number(values, 'bonus_amount')
  const currency = text(values, 'currency')
  const subject = labelOf(values, 'employee_id') ?? labelOf(values, 'department_id') ?? '—'
  const periodText = from && to ? `${day(from, locale)} — ${day(to, locale)}` : '—'

  const primary: HeaderAction | null =
    mayApprove && employeeId && from && to ? { id: 'bonus', label: t('record.hr.bonus.open'), onClick: () => setDialog('bonus') } : null
  const secondary: HeaderAction[] =
    mayUpdate && employeeId
      ? [{ id: 'recompute', label: t('record.hr.kpi.recompute'), icon: 'refresh', onClick: () => recompute.mutate(), disabled: recompute.isPending }]
      : []
  const menu: MenuAction[] = [
    {
      id: 'copy-link',
      label: t('record.copyLink'),
      icon: 'link',
      onSelect: () => void navigator.clipboard?.writeText(window.location.href).catch(() => undefined),
    },
  ]
  if (mayUpdate && manual) {
    menu.unshift({ id: 'actual', label: t('record.hr.kpi.setActual'), icon: 'settings', onSelect: () => setDialog('actual') })
  }

  // ------------------------------------------------------------------ полоса и факты

  const now = today()
  const periodState = from && to ? (now > to ? 'done' : now >= from ? 'current' : 'future') : 'future'
  const settled = hasBonusColumn && bonusAmount != null
  const steps = [
    { id: 'set', label: t('record.hr.kpi.stageSet'), state: 'done' as const, meta: day(dateOf(text(values, 'created_at')), locale) },
    { id: 'period', label: t('record.hr.kpi.stagePeriod'), state: periodState as 'done' | 'current' | 'future', meta: periodText },
    {
      id: 'bonus',
      label: t('record.hr.kpi.stageBonus'),
      state: (settled ? 'done' : periodState === 'done' ? 'current' : 'future') as 'done' | 'current' | 'future',
      meta: !hasBonusColumn ? t('record.hr.kpi.bonusHidden') : settled ? money(bonusAmount, currency, locale) : t('record.hr.kpi.notSettled'),
    },
  ]

  const facts: Fact[] = [
    {
      id: 'overall',
      label: t('record.hr.kpi.overall'),
      value: dashboard.data ? percent(dashboard.data.overallPercent, locale) : '—',
      sub: t('record.hr.kpi.overallNote'),
    },
    {
      id: 'target',
      label: labelOf(values, 'kpi_id') ?? t('card.hr.kpi.metric'),
      value: `${kpiValue(number(values, 'actual_value'), definition?.unit, locale)} / ${kpiValue(number(values, 'target_value'), definition?.unit, locale)}`,
      sub: t('record.hr.kpi.actualOfTarget'),
    },
    {
      id: 'completion',
      label: t('card.hr.kpi.completion'),
      value: target ? percent(target.completionPercent, locale) : '—',
    },
    {
      id: 'bonus',
      label: t('column.bonus_amount'),
      // No field grant — the server leaves the column out; a dash, not zero.
      value: hasBonusColumn ? money(bonusAmount, currency, locale) : '—',
      sub: hasBonusColumn ? (settled ? undefined : t('record.hr.kpi.notSettled')) : t('record.hr.kpi.bonusHidden'),
    },
    { id: 'period', label: t('record.hr.kpi.period'), value: periodText },
  ]

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.kpi'), to: listPath('hr.kpi', '/kpi') }, { label: subject }]}
        title={t('record.hr.kpi.title', { subject, period: periodText })}
        meta={[
          employeeId ? <RefLink key="emp" values={values} column="employee_id" entity="hr.employee" avatar /> : null,
          departmentId ? (
            <span key="dept" className="row" style={{ gap: 6 }}>
              <Icon name="building" size="sm" />
              <RefLink values={values} column="department_id" entity="hr.department" />
            </span>
          ) : null,
          labelOf(values, 'kpi_id') ? <span key="kpi">{labelOf(values, 'kpi_id')}</span> : null,
        ]}
        primary={primary}
        secondary={secondary}
        menu={menu}
        menuLabel={t('record.more')}
      />

      <OutcomeAlerts failure={outcome.failure} notice={outcome.notice} />
      {employeeId == null && (
        // A department goal belongs to nobody personally — said in words, so an absent
        // bonus does not read as "not earned".
        <div className="alert alert-neutral" role="status">
          <Icon name="building" />
          <span>{t('card.hr.kpi.departmentGoal')}</span>
        </div>
      )}

      <StageBar steps={steps} ariaLabel={t('record.hr.kpi.stages')} />
      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'lines', label: t('card.tab.hrKpiTargets'), count: dashboard.data?.lines.length ?? null },
          ...(employeeId ? [{ id: 'periods', label: t('record.hr.kpi.periods'), count: targets.data?.length ?? null }] : []),
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.hr.kpi.sections')}
      >
        <div className="layout-2-wide" style={{ paddingTop: 14 }}>
          <div className="rec-col">
            {tab === 'lines' && (
              <Panel title={t('record.hr.kpi.linesTitle', { period: periodText })} flush>
                {from == null || to == null ? (
                  <StateView compact kind="error" title={t('record.hr.kpi.noPeriod')} />
                ) : (
                  <Section query={dashboard} errorTitle={t('record.hr.kpi.failed')}>
                    {(data: DashboardView) =>
                      data.lines.length === 0 ? (
                        <StateView compact kind="empty" title={t('card.hr.kpi.noTargets')} description={t('record.hr.kpi.noTargetsText')} />
                      ) : (
                        <>
                          <KpiLines data={data} weights />
                          <div className="panel-f">
                            <span className="secondary">{t('card.hr.kpi.overall', { percent: num(data.overallPercent, locale) })}</span>
                          </div>
                        </>
                      )
                    }
                  </Section>
                )}
              </Panel>
            )}
            {tab === 'periods' && employeeId && <TargetsPanel employeeId={employeeId} currentId={recordId} />}
            {tab === 'history' && (
              <Panel title={t('record.history.changes')}>
                <HistoryPanel entity={context.entity} recordId={recordId} />
              </Panel>
            )}
          </div>
          <div className="rec-col">
            <Panel title={t('record.hr.details')}>{form}</Panel>
            {!mayApprove && employeeId && (
              <Panel title={t('record.hr.kpi.bonusTitle')}>
                <p className="secondary">{t('card.hr.kpi.bonusElsewhere')}</p>
              </Panel>
            )}
          </div>
        </div>
      </RecordTabs>

      {dialog === 'bonus' && employeeId && from && to && (
        <BonusDialog
          employeeId={employeeId}
          name={subject}
          period={{ from, to }}
          onClose={() => setDialog(null)}
          onDone={(bonus) => {
            setDialog(null)
            outcome.succeed(t('record.hr.bonus.settled', { amount: money(bonus.bonus, null, locale) }))
          }}
        />
      )}
      {dialog === 'actual' && (
        <ActualDialog
          targetId={recordId}
          name={labelOf(values, 'kpi_id') ?? ''}
          current={number(values, 'actual_value')}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null)
            outcome.succeed(t('record.hr.kpi.actualSaved'))
          }}
        />
      )}
    </div>
  )
}

/**
 * Цели сотрудника по периодам: план и факт двумя числами, а не одним процентом — процент
 * теряет масштаб, а он и есть предмет разговора о премии. Премия — под полевым правом: без
 * него сервер шлёт `null`, и рисуется прочерк, а не ноль.
 */
export function TargetsPanel({ employeeId, currentId }: { employeeId: string; currentId?: string }) {
  const { t, user, locale } = useApp()
  const query = useQuery({
    queryKey: ['hr-targets', employeeId],
    queryFn: ({ signal }) => fetchTargets(employeeId, signal),
    retry: false,
  })
  // Единица — из определения показателя; пока оно не пришло, числа голые, а не в
  // выдуманной единице.
  const definitions = useQuery({
    queryKey: ['hr-kpi-definitions'],
    queryFn: ({ signal }) => fetchDefinitions(signal),
    retry: false,
  })
  const unitOf = (kpiId: string) => (definitions.data ?? []).find((row) => row.id === kpiId)?.unit ?? null
  return (
    <Panel title={t('record.hr.kpi.periods')} flush>
      <Section query={query} errorTitle={t('record.hr.kpi.failed')}>
        {(rows: TargetView[]) =>
          rows.length === 0 ? (
            <StateView compact kind="empty" title={t('card.hr.kpi.noTargets')} />
          ) : (
            <table className="tbl" aria-label={t('record.hr.kpi.periods')}>
              <thead>
                <tr>
                  <th>{t('record.hr.kpi.period')}</th>
                  <th className="num">{t('column.target_value')}</th>
                  <th className="num">{t('column.actual_value')}</th>
                  <th className="num">{t('card.hr.kpi.completion')}</th>
                  <th className="num">{t('column.bonus_amount')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const href = row.id === currentId ? null : cardHref(user, 'hr.kpi', row.id)
                  const period = `${day(row.periodStart, locale)} — ${day(row.periodEnd, locale)}`
                  return (
                    <tr key={row.id} className={row.id === currentId ? 'is-selected' : undefined}>
                      <td>{href ? <Link to={href}>{period}</Link> : period}</td>
                      <td className="num">{kpiValue(row.target, unitOf(row.kpiId), locale)}</td>
                      <td className="num">{kpiValue(row.actual, unitOf(row.kpiId), locale)}</td>
                      <td className="num">{percent(row.completionPercent, locale)}</td>
                      <td className="num">{money(row.bonusAmount, row.currency, locale)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )
        }
      </Section>
    </Panel>
  )
}

/** Ручной факт — только показателю без источника (кнопку остальным не рисуют). */
function ActualDialog({
  targetId,
  name,
  current,
  onClose,
  onDone,
}: {
  targetId: string
  name: string
  current: number | null
  onClose: () => void
  onDone: () => void
}) {
  const { t, locale } = useApp()
  const [value, setValue] = useState(current == null ? '' : String(current))
  const [failure, setFailure] = useState<string | null>(null)
  const parsed = Number(value.replace(',', '.').replace(/\s/g, ''))
  const save = useMutation({
    mutationFn: () => setActual(targetId, parsed),
    onSuccess: onDone,
    onError: (error) => setFailure(failureText(error, t)),
  })
  return (
    <FormModal
      open
      title={t('record.hr.kpi.setActual')}
      subtitle={name}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('card.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={value.trim() === '' || !Number.isFinite(parsed) || save.isPending}
            onClick={() => save.mutate()}
          >
            {t('card.save')}
          </button>
        </>
      }
    >
      {failure && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{failure}</span>
        </div>
      )}
      <div className="form-grid">
        <div className="field">
          <span className="label">{t('record.hr.kpi.was')}</span>
          <div className="input is-readonly">{num(current, locale)}</div>
        </div>
        <Field id="hr-kpi-actual" label={t('record.hr.kpi.becomes')} required>
          <input id="hr-kpi-actual" className="input num" inputMode="decimal" value={value} onChange={(event) => setValue(event.target.value)} />
        </Field>
      </div>
      <span className="help">{t('record.hr.kpi.manualHelp')}</span>
    </FormModal>
  )
}

function dateOf(moment: string | null): string | null {
  if (!moment) return null
  const parsed = new Date(moment)
  if (Number.isNaN(parsed.getTime())) return null
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`
}
